/**
 * Score Validator for Laudo Quality Assessment (Frontend)
 *
 * Validates LLM-generated scores against the TEA Laudo Quality Scale rubric.
 * Mirrors backend/src/utils/scoreValidator.js logic.
 */

export const SCORING_RUBRIC = {
  '1.1': { max: 15, label: 'Critérios Diagnósticos' },
  '1.2': { max: 10, label: 'Classificação de Gravidade' },
  '2.1': { max: 12, label: 'Métodos de Avaliação' },
  '2.2': { max: 8,  label: 'Observações Clínicas' },
  '3.1': { max: 10, label: 'Histórico do Desenvolvimento' },
  '3.2': { max: 5,  label: 'Informações Contextuais' },
  '4.1': { max: 10, label: 'Avaliação Funcional' },
  '4.2': { max: 5,  label: 'Impacto na Vida Diária' },
  '5.1': { max: 10, label: 'Estratégias de Intervenção' },
  '5.2': { max: 5,  label: 'Coordenação de Serviços' },
  '6.1': { max: 5,  label: 'Qualidade do Documento' },
  '6.2': { max: 5,  label: 'Ações e Acompanhamento' },
};

export const DOMAIN_MAXES = {
  '1': 25, '2': 20, '3': 15, '4': 15, '5': 15, '6': 10
};

export const DOMAINS = {
  '1': ['1.1', '1.2'],
  '2': ['2.1', '2.2'],
  '3': ['3.1', '3.2'],
  '4': ['4.1', '4.2'],
  '5': ['5.1', '5.2'],
  '6': ['6.1', '6.2']
};

export const TOTAL_MAX = 100;

export function parseScoreReport(reportMarkdown) {
  if (!reportMarkdown) return { criteria: {}, domainSubtotals: {} };

  const criteria = {};
  const domainSubtotals = {};

  for (const domainKey of Object.keys(DOMAINS)) {
    const subtotalPattern = new RegExp(`Subtotal\\s*Domínio\\s*${domainKey}[^\\d]*(\\d+)`, 'i');
    const match = reportMarkdown.match(subtotalPattern);
    if (match) {
      domainSubtotals[domainKey] = parseInt(match[1]);
    }
  }

  for (const criterionKey of Object.keys(SCORING_RUBRIC)) {
    const esc = criterionKey.replace('.', '\\.');
    const patterns = [
      new RegExp(`\\|\\s*\\*{0,2}${esc}\\b[^\\n]*?\\*\\*(\\d+)\\*\\*`, 'i'),
      new RegExp(`\\|\\s*\\*{0,2}${esc}\\*{0,2}\\s*\\|[^\\|]*\\|[^\\|]*\\|\\s*(\\d+)`, 'i'),
      new RegExp(`(?:^|\\n)\\s*(?:#{1,4}\\s+)?\\*{0,2}${esc}\\b[^\\d]*(\\d+)`, 'im')
    ];

    for (const pattern of patterns) {
      const match = reportMarkdown.match(pattern);
      if (match) {
        criteria[criterionKey] = parseInt(match[1]);
        break;
      }
    }
  }

  return { criteria, domainSubtotals };
}

export function validateScores(parsed) {
  const warnings = [];
  const correctedCriteria = {};
  const correctedDomains = {};
  let total = 0;

  for (const [domainKey, criteriaKeys] of Object.entries(DOMAINS)) {
    const reportedSubtotal = parsed.domainSubtotals[domainKey];

    let domainFromCriteria = 0;
    for (const key of criteriaKeys) {
      const raw = parsed.criteria[key];
      if (raw !== undefined) {
        const max = SCORING_RUBRIC[key].max;
        if (raw > max) {
          warnings.push(`${key} (${SCORING_RUBRIC[key].label}): pontuação ${raw} excede máximo de ${max}, ajustado para ${max}`);
          correctedCriteria[key] = max;
        } else {
          correctedCriteria[key] = raw;
        }
        domainFromCriteria += correctedCriteria[key];
      }
    }

    let domainScore;
    if (reportedSubtotal !== undefined) {
      const domainMax = DOMAIN_MAXES[domainKey];
      if (reportedSubtotal > domainMax) {
        warnings.push(`Domínio ${domainKey}: subtotal ${reportedSubtotal} excede máximo de ${domainMax}, ajustado para ${domainMax}`);
        domainScore = domainMax;
      } else {
        domainScore = reportedSubtotal;
      }

      if (domainFromCriteria > 0 && domainScore !== domainFromCriteria) {
        const safeScore = Math.min(domainScore, domainFromCriteria);
        if (safeScore !== domainScore) {
          warnings.push(`Domínio ${domainKey}: subtotal ${domainScore} não confere com soma dos critérios ${domainFromCriteria}, usando ${safeScore}`);
        }
        domainScore = safeScore;
      }
    } else {
      domainScore = Math.min(domainFromCriteria, DOMAIN_MAXES[domainKey]);
    }

    correctedDomains[domainKey] = domainScore;
    total += domainScore;
  }

  if (total > TOTAL_MAX) {
    warnings.push(`Total ${total} excede máximo de ${TOTAL_MAX}, ajustado para ${TOTAL_MAX}`);
    total = TOTAL_MAX;
  }

  if (total >= 90 && warnings.length === 0) {
    warnings.push(`Pontuação ${total}/100 (Excepcional) - este nível é extremamente raro e deve ser verificado manualmente`);
  }

  return {
    valid: warnings.length === 0 || (warnings.length === 1 && total >= 90),
    total,
    warnings,
    details: {
      criteria: correctedCriteria,
      domains: correctedDomains
    }
  };
}

export function getClassification(total) {
  if (total >= 90) return 'Excepcional';
  if (total >= 70) return 'Bom';
  if (total >= 50) return 'Adequado';
  if (total >= 30) return 'Fraco';
  return 'Inadequado';
}
